import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  useTheme,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import HotelIcon from "@mui/icons-material/Hotel";
import SavingsIcon from "@mui/icons-material/Savings";
import { VM_Colors } from "../../Styles/VMStyles";
import { API_GET_REVENUE_INVOICE_INFO } from "../../Util/URLConstants";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_COUNTRY_DATA_KEY, SESSION_USER_ID_KEY } from "../../Util/VMConstants";
import MUITypography from "../../Components/MUITypography";
import { formatAmount } from "../../Util/UtilHelper";

const RevenueChecker = ({ selectedMonth, selectedYear }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  const fetchMonthReport = async (userId, month, year) => {
    try {
      const response = await fetch(
        `${API_GET_REVENUE_INVOICE_INFO}?user_id=${userId}&month=${month}&year=${year}`
      );
      const result = await response.json();
      return result || {};
    } catch (err) {
      console.error("Error:", err.message);
      return null;
    }
  };

  useEffect(() => {
    if (!userID) return;
    setLoading(true);
    fetchMonthReport(userID, selectedMonth, selectedYear)
      .then(setReportData)
      .finally(() => setLoading(false));
  }, [userID, selectedMonth, selectedYear]);

  const {
    venue_charges = 0,
    food_charges = 0,
    accommodation_charges = 0,
    additional_charges = 0,
  } = reportData || {};

  const totalRevenue =
    venue_charges + food_charges + accommodation_charges + additional_charges;

  const revenueCards = [
    {
      label: "Venue Charges",
      value: `${formatAmount( venue_charges,currencySymbol)}`,
      icon: (
        <HomeWorkIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} />
      ),
    },
    {
      label: "Food Charges",
      value: `${formatAmount( food_charges,currencySymbol)}`,
      icon: (
        <RestaurantIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} />
      ),
    },
    {
      label: "Accommodation Charges",
      value: `${formatAmount(accommodation_charges,currencySymbol)}`,
      icon: (
        <HotelIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} />
      ),
    },
    {
      label: "Additional Charges",
      value: `${formatAmount( additional_charges,currencySymbol)}`,
      icon: (
        <SavingsIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} />
      ),
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: VM_Colors.white,
        borderRadius: 3,
        padding: 3,
        paddingBottom: 5,
        boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <MUITypography  variant='h4' fontWeight={700}>
          Revenue Checker
        </MUITypography>
        {/* <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box> */}
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          borderRadius: 1.5,
          paddingY: 1.5,
          paddingX: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "white",
          mb: 3,
        }}
      >
        <Typography fontSize={16}>Total Revenue Generated</Typography>
        {loading ? (
          <CircularProgress size={24} sx={{ color: "white" }} />
        ) : (
          <Typography fontSize={22} fontWeight={600}>
            {formatAmount( totalRevenue,currencySymbol) }
          </Typography>
        )}
      </Box>

      <Grid container rowSpacing={5} columnSpacing={3}>
        {revenueCards.map((item, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Box
              sx={{
                position: "relative",
                border: "1px solid #F0F0F0",
                borderRadius: 2.5,
                backgroundColor: "#FFFFFF",
                padding: 2.5,
                minWidth: 200,
                paddingTop: "30px",
                paddingBottom: "30px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.03)",
                transition: "box-shadow 0.3s ease",
                "&:hover": {
                  boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.06)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </Box>

              <Box sx={{ pr: 5 }}>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color="text.secondary"
                  gutterBottom
                >
                  {item.label}
                </Typography>
                <Typography fontSize={20} fontWeight={700}>
                  {loading ? "Loading..." : item.value}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RevenueChecker;
